import { useOrganization, useUser } from '@clerk/clerk-react'
import { FileSignature, Plus, Pencil, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import {
  useSigningConfigurationQuery,
  useSigningPersistence,
} from './persistence'
import type {
  CatalogForm,
  PrefillSummary,
  OrganizationSigningSettings,
} from './persistence'
import { useOrgTools, useUpdateTool } from '@/hooks/useToolsAdmin'

export function SigningToolEntry() {
  const [open, setOpen] = useState(false)
  const { data: tools } = useOrgTools()
  const updateTool = useUpdateTool()
  const managesTool = useIsCareforceOrgAdmin()
  const settings = useSigningConfigurationQuery<OrganizationSigningSettings>(
    'settings',
    !managesTool
  )
  const persistence = useSigningPersistence()
  const [toggling, setToggling] = useState(false)
  const tool = tools?.find((item) => String(item.kind) === 'signing_documents')
  const enabled =
    (managesTool ? tool?.enabled : settings.data?.enabled) ?? false
  return (
    <>
      <div className="flex w-full items-center gap-3 rounded-lg border border-neutral-ui-border p-3">
        <FileSignature className="size-5 shrink-0 text-primary-high-contrast" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">Document signing</span>
            <Badge variant="outline">Managed by Careforce</Badge>
            {tool?.david_enabled && <Badge variant="outline">David</Badge>}
            {tool?.angelica_enabled && (
              <Badge variant="outline">Angelica</Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Prepare documents with member information and collect signatures.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={
            toggling ||
            (managesTool
              ? !tool || tool.read_only || updateTool.isPending
              : !settings.isSuccess)
          }
          className="cursor-pointer"
          aria-label={`${enabled ? 'Disable' : 'Enable'} Document signing`}
          onCheckedChange={async (next) => {
            setToggling(true)
            try {
              if (managesTool) {
                if (!tool || tool.read_only) return
                await updateTool.mutateAsync({
                  toolId: tool.id,
                  tool: { enabled: next },
                })
              } else await persistence.setOrganizationEnabled(next)
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : 'Could not update Document signing.'
              )
            } finally {
              setToggling(false)
            }
          }}
        />
        {!managesTool && settings.isError && (
          <Button
            variant="link"
            size="sm"
            onClick={() => void settings.refetch()}>
            Retry
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Edit Document signing"
          onClick={() => setOpen(true)}>
          <Pencil className="size-4" />
        </Button>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        {open && <SigningToolDetails onClose={() => setOpen(false)} />}
      </Sheet>
    </>
  )
}

function SigningToolDetails({ onClose }: { onClose: () => void }) {
  const { user } = useUser()
  const { organization } = useOrganization()
  const managesTemplates = useIsCareforceOrgAdmin()
  const { toTask } = useAppNavigate()
  const [creatingFor, setCreatingFor] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [invalidName, setInvalidName] = useState(false)
  const formsQuery = useSigningConfigurationQuery<{ forms: CatalogForm[] }>(
    'forms'
  )
  const configurationsQuery = useSigningConfigurationQuery<{
    prefills: PrefillSummary[]
  }>('prefills')
  const persistence = useSigningPersistence()
  const [creating, setCreating] = useState(false)
  const forms = formsQuery.data?.forms ?? []
  const configurations = configurationsQuery.data?.prefills ?? []
  return (
    <SheetContent
      showCloseButton={false}
      className="w-full gap-0 overflow-y-auto p-0 sm:max-w-xl">
      <SheetHeader>
        <div className="flex items-center gap-2">
          <FileSignature className="size-5" />
          <SheetTitle className="min-w-0 flex-1">Document signing</SheetTitle>
          <SheetClose asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Close">
              <X className="size-4" />
            </Button>
          </SheetClose>
        </div>
        <SheetDescription className="sr-only">
          Document signing configurations.
        </SheetDescription>
      </SheetHeader>
      <div className="space-y-6 p-5">
        <section
          aria-label="New templates"
          className="rounded-lg bg-muted/40 px-3 py-3">
          <h2 className="text-xs font-medium">New templates</h2>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {managesTemplates
              ? 'Create the template in PandaDoc, then ask David to import it and configure its fields.'
              : 'Careforce adds templates through David and PandaDoc. Ask Careforce to add a document.'}
          </p>
        </section>
        {(formsQuery.isPending || configurationsQuery.isPending) && (
          <p className="text-sm text-muted-foreground">Loading documents…</p>
        )}
        {(formsQuery.isError || configurationsQuery.isError) && (
          <div role="alert" className="text-sm">
            Could not load documents.
            <Button
              variant="link"
              onClick={() => {
                void formsQuery.refetch()
                void configurationsQuery.refetch()
              }}>
              Retry
            </Button>
          </div>
        )}
        {formsQuery.isSuccess && !forms.length && (
          <p className="text-sm text-muted-foreground">
            No document templates are available.
          </p>
        )}
        {forms.map((form) => {
          const configs = configurations.filter(
            (item) => item.form_key === form.key
          )
          return (
            <section
              key={form.key}
              aria-label={`${form.key.toUpperCase()} configurations`}>
              <div className="mb-1 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">
                  {form.key.toUpperCase()}
                </h2>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Add ${form.key.toUpperCase()} configuration`}
                  onClick={() => {
                    let nextName = configs.length
                      ? 'New configuration'
                      : 'Default'
                    let count = 2
                    while (
                      configs.some(
                        (item) =>
                          item.name.toLowerCase() === nextName.toLowerCase()
                      )
                    )
                      nextName = `New configuration ${count++}`
                    setName(nextName)
                    setInvalidName(false)
                    setCreatingFor(form.key)
                  }}>
                  <Plus className="size-4" />
                </Button>
              </div>
              {managesTemplates && form.org_slug === 'careforce' && (
                <Button
                  variant="ghost"
                  className="mb-1 w-full justify-between font-normal text-muted-foreground"
                  onClick={() => {
                    onClose()
                    toTask(`/tools/document-signing/${form.key}`)
                  }}>
                  <span>Careforce settings</span>
                  <Pencil className="size-3.5" />
                </Button>
              )}
              {configs.map((config) => (
                <Button
                  key={config.id}
                  type="button"
                  variant="ghost"
                  className="w-full justify-between text-left font-normal"
                  onClick={() => {
                    onClose()
                    toTask(`/tools/document-signing/${form.key}/${config.id}`)
                  }}>
                  <span className="min-w-0 flex-1 truncate">{config.name}</span>
                  <Pencil className="size-3.5 text-muted-foreground" />
                </Button>
              ))}
              {creatingFor === form.key && (
                <form
                  className="mt-2 flex flex-wrap items-center gap-2 px-3"
                  onSubmit={async (event) => {
                    event.preventDefault()
                    if (!user || !organization) return
                    if (
                      !name.trim() ||
                      configs.some(
                        (item) =>
                          item.name.toLowerCase() === name.trim().toLowerCase()
                      )
                    ) {
                      setInvalidName(true)
                      return
                    }
                    setCreating(true)
                    try {
                      const config = await persistence.createConfiguration(
                        form.key,
                        name
                      )
                      onClose()
                      toTask(`/tools/document-signing/${form.key}/${config.id}`)
                    } catch (error) {
                      toast.error(
                        error instanceof Error
                          ? error.message
                          : 'Could not create configuration.'
                      )
                    } finally {
                      setCreating(false)
                    }
                  }}>
                  <Input
                    autoFocus
                    aria-label="New configuration name"
                    aria-invalid={invalidName || undefined}
                    className="min-w-32 flex-1"
                    value={name}
                    onChange={(event) => {
                      setName(event.target.value)
                      setInvalidName(false)
                    }}
                  />
                  <Button type="submit" size="sm" disabled={creating}>
                    {creating ? 'Creating…' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCreatingFor(null)}>
                    Cancel
                  </Button>
                </form>
              )}
            </section>
          )
        })}
      </div>
    </SheetContent>
  )
}
